import { useMemo, useState } from "react";
import { LTE_CLOSED_STATUSES, type LteCommand, type LteTask } from "../api/lte";
import { toErrorMessage } from "../api/client";
import { Icons } from "../components/Icons";
import { ScreenHeader } from "../components/ScreenHeader";
import { Sheet } from "../components/Sheet";
import {
  useDeclareLteException,
  useLteCommand,
  useLteTasks,
  useSubmitLteProof,
} from "../hooks/useLte";
import {
  chipMeta,
  LTE_PRIORITY_META,
  LTE_STATUS_META,
  LTE_TYPE_LABEL,
} from "../lib/statusMeta";

/** Actions permises par statut : miroir des arêtes worker de la machine à états
 * LTE (statemachine.go). Exclut les recouvrements silencieux (unassign/release)
 * et l'affectation superviseur (assign, écran équipe). Toute arête légale du
 * backend doit rester atteignable, sinon la tâche se bloque sans action UI. */
const ACTIONS_BY_STATUS: Record<string, LteCommand[]> = {
  assigned: ["accept", "reject", "block", "cancel"],
  accepted: ["start", "block", "cancel"],
  started: ["pause", "block", "complete", "cancel"],
  paused: ["resume", "block", "cancel"],
  blocked: ["resume", "pause", "cancel"],
};

const ACTION_LABEL: Record<LteCommand, string> = {
  accept: "Accepter",
  start: "Démarrer",
  pause: "Pause",
  resume: "Reprendre",
  block: "Signaler un blocage",
  complete: "Terminer",
  cancel: "Annuler",
  reject: "Refuser l'affectation",
};

const REASON_REQUIRED: LteCommand[] = ["block", "cancel", "reject"];

/* ── Filtres par statut (chips de la maquette) ── */
type TerrainFilter = "all" | "blocked" | "active" | "todo" | "planned" | "done";
const FILTER_DEFS: {
  key: TerrainFilter;
  label: string;
  statuses: LteTask["status"][] | null;
}[] = [
  { key: "all", label: "Toutes", statuses: null },
  { key: "blocked", label: "Bloquées", statuses: ["blocked"] },
  { key: "active", label: "En cours", statuses: ["started", "paused"] },
  { key: "todo", label: "À démarrer", statuses: ["accepted", "assigned"] },
  { key: "planned", label: "Planifiées", statuses: ["planned"] },
  {
    key: "done",
    label: "Terminées",
    statuses: ["completed", "cancelled", "rejected"],
  },
];

/** Tri d'urgence de la maquette : bloquées en tête, closes en queue. */
const STATUS_RANK: Record<string, number> = {
  blocked: 0,
  paused: 1,
  started: 2,
  accepted: 3,
  assigned: 3,
  planned: 4,
  completed: 5,
  cancelled: 6,
  rejected: 6,
};

function taskLabel(t: LteTask): string {
  const type = LTE_TYPE_LABEL[t.execution_type] ?? t.execution_type;
  const where = t.zone_ref || t.site_ref;
  return where ? `${type} · ${where}` : type;
}

function dueText(t: LteTask): string | null {
  if (!t.due_end) return null;
  return new Date(t.due_end).toLocaleString("fr-FR", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Chip({
  meta,
}: {
  meta: { label: string; bg: string; color: string };
}) {
  return (
    <span className="chip" style={{ background: meta.bg, color: meta.color }}>
      {meta.label}
    </span>
  );
}

/* ── Types de preuve (chips de la maquette) ── */
const PROOF_TYPES: { key: string; label: string }[] = [
  { key: "photo", label: "Photo" },
  { key: "signature", label: "Signature" },
  { key: "scan", label: "Scan" },
  { key: "checklist", label: "Checklist" },
  { key: "measure", label: "Mesure" },
  { key: "comment", label: "Commentaire" },
  { key: "geolocation", label: "Géolocalisation" },
];

function TaskDetailSheet({
  task,
  onClose,
  initialReasonFor = null,
  initialProofOpen = false,
}: {
  task: LteTask;
  onClose: () => void;
  initialReasonFor?: LteCommand | null;
  initialProofOpen?: boolean;
}) {
  const command = useLteCommand();
  const proof = useSubmitLteProof();
  const exception = useDeclareLteException();
  const [reasonFor, setReasonFor] = useState<LteCommand | null>(
    initialReasonFor,
  );
  const [reason, setReason] = useState("");
  const [proofOpen, setProofOpen] = useState(initialProofOpen);
  const [proofType, setProofType] = useState("photo");
  const [proofUri, setProofUri] = useState("");
  const [excOpen, setExcOpen] = useState(false);
  const [excKind, setExcKind] = useState("");
  const [excSeverity, setExcSeverity] = useState("moyenne");
  const [error, setError] = useState<string | null>(null);

  const actions = ACTIONS_BY_STATUS[task.status] ?? [];

  const run = (cmd: LteCommand, cmdReason?: string) => {
    setError(null);
    command.mutate(
      { id: task.id, command: cmd, version: task.version, reason: cmdReason },
      {
        onSuccess: () => {
          setReasonFor(null);
          setReason("");
        },
        onError: (e) => setError(toErrorMessage(e)),
      },
    );
  };

  const onAction = (cmd: LteCommand) => {
    if (REASON_REQUIRED.includes(cmd)) {
      setReasonFor(cmd);
    } else {
      run(cmd);
    }
  };

  return (
    <Sheet title={taskLabel(task)} onClose={onClose}>
      <div className="row-line">
        <Chip meta={chipMeta(LTE_STATUS_META, task.status)} />
        <Chip meta={chipMeta(LTE_PRIORITY_META, task.priority)} />
      </div>

      <div className="lte-grid">
        <div className="lte-cell">
          <div className="k">Site / Zone</div>
          <div className="v">
            {[task.site_ref, task.zone_ref].filter(Boolean).join(" · ") || "-"}
          </div>
        </div>
        <div className="lte-cell">
          <div className="k">Type</div>
          <div className="v">
            {LTE_TYPE_LABEL[task.execution_type] ?? task.execution_type}
          </div>
        </div>
        <div className="lte-cell">
          <div className="k">Échéance</div>
          <div className="v mono">{dueText(task) ?? "-"}</div>
        </div>
        <div className="lte-cell">
          <div className="k">Preuves requises</div>
          <div className="v">
            {(task.required_proofs?.length ?? 0) > 0
              ? task.required_proofs?.join(", ")
              : "Aucune"}
          </div>
        </div>
      </div>

      {task.status === "blocked" && (
        <div className="block-note">
          Blocage signalé : reprenez ou mettez en pause pour débloquer.
        </div>
      )}

      {error && <div className="form-error">{error}</div>}

      {reasonFor ? (
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!reason.trim()) return;
            run(reasonFor, reason.trim());
          }}
        >
          <label>
            Motif ({ACTION_LABEL[reasonFor]})
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex. accès verrouillé, matériel manquant…"
              autoFocus
            />
          </label>
          <div className="sheet-cta">
            <button
              type="submit"
              className={`btn ${reasonFor === "block" || reasonFor === "reject" ? "" : "primary"}`}
              style={
                reasonFor === "block" || reasonFor === "reject"
                  ? { background: "var(--danger)", color: "var(--n-0)" }
                  : undefined
              }
              disabled={command.isPending || !reason.trim()}
            >
              Confirmer
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => setReasonFor(null)}
            >
              Retour
            </button>
          </div>
        </form>
      ) : (
        <div className="sheet-cta wrap">
          {actions.map((cmd) => (
            <button
              key={cmd}
              className={`btn ${
                cmd === "complete"
                  ? "success"
                  : cmd === "start" || cmd === "accept" || cmd === "resume"
                    ? "primary"
                    : ""
              }`}
              disabled={command.isPending}
              onClick={() => onAction(cmd)}
            >
              {ACTION_LABEL[cmd]}
            </button>
          ))}
        </div>
      )}

      {["started", "paused", "blocked", "completed"].includes(task.status) && (
        <div className="sub-section">
          <div className="section-title" style={{ padding: "0 0 8px" }}>
            Preuves
          </div>
          {proofOpen ? (
            <form
              className="inline-form"
              onSubmit={(e) => {
                e.preventDefault();
                proof.mutate(
                  {
                    id: task.id,
                    proof_type: proofType,
                    ...(proofUri.trim() ? { proof_uri: proofUri.trim() } : {}),
                  },
                  {
                    onSuccess: () => {
                      setProofOpen(false);
                      setProofUri("");
                    },
                    onError: (e2) => setError(toErrorMessage(e2)),
                  },
                );
              }}
            >
              <div className="choice-chips">
                {PROOF_TYPES.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    className={`choice-chip${proofType === p.key ? " active" : ""}`}
                    onClick={() => setProofType(p.key)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <label>
                URI de la preuve (optionnel)
                <input
                  value={proofUri}
                  onChange={(e) => setProofUri(e.target.value)}
                  placeholder="dam://… ou https://…"
                />
              </label>
              <div className="sheet-cta">
                <button
                  type="submit"
                  className="btn primary"
                  disabled={proof.isPending}
                >
                  Soumettre
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setProofOpen(false)}
                >
                  Annuler
                </button>
              </div>
            </form>
          ) : (
            <button className="btn ghost" onClick={() => setProofOpen(true)}>
              + Soumettre une preuve
            </button>
          )}
        </div>
      )}

      {!LTE_CLOSED_STATUSES.includes(task.status) && (
        <div className="sub-section">
          {excOpen ? (
            <form
              className="inline-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (!excKind.trim()) return;
                exception.mutate(
                  {
                    id: task.id,
                    kind: excKind.trim(),
                    severity: excSeverity,
                  },
                  {
                    onSuccess: () => {
                      setExcOpen(false);
                      setExcKind("");
                    },
                    onError: (e2) => setError(toErrorMessage(e2)),
                  },
                );
              }}
            >
              <label>
                Nature de l'exception
                <input
                  value={excKind}
                  onChange={(e) => setExcKind(e.target.value)}
                  placeholder="Ex. accès impossible"
                />
              </label>
              <label>
                Sévérité
                <select
                  value={excSeverity}
                  onChange={(e) => setExcSeverity(e.target.value)}
                >
                  <option value="haute">Élevée</option>
                  <option value="moyenne">Moyenne</option>
                  <option value="faible">Faible</option>
                </select>
              </label>
              <div className="sheet-cta">
                <button
                  type="submit"
                  className="btn primary"
                  disabled={exception.isPending || !excKind.trim()}
                >
                  Déclarer
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setExcOpen(false)}
                >
                  Retour
                </button>
              </div>
            </form>
          ) : (
            <button className="btn ghost" onClick={() => setExcOpen(true)}>
              ⚠ Déclarer une exception
            </button>
          )}
        </div>
      )}
    </Sheet>
  );
}

/* ── Écran ── */
export function TerrainScreen() {
  const { data, isLoading, isError } = useLteTasks();
  const command = useLteCommand();
  const [filter, setFilter] = useState<TerrainFilter>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailReasonFor, setDetailReasonFor] = useState<LteCommand | null>(
    null,
  );
  const [detailProofOpen, setDetailProofOpen] = useState(false);

  const tasks = useMemo(() => data?.items ?? [], [data]);
  const selected = tasks.find((t) => t.id === selectedId) ?? null;

  const counts = {
    blocked: tasks.filter((t) => t.status === "blocked").length,
    running: tasks.filter((t) => ["started", "paused"].includes(t.status))
      .length,
    planned: tasks.filter((t) => t.status === "planned").length,
  };

  const activeStatuses = FILTER_DEFS.find((f) => f.key === filter)?.statuses;
  const visible = tasks
    .filter((t) => !activeStatuses || activeStatuses.includes(t.status))
    .sort(
      (a, b) => (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9),
    );

  const openDetail = (
    id: string,
    reasonFor: LteCommand | null = null,
    proofOpen = false,
  ) => {
    setSelectedId(id);
    setDetailReasonFor(reasonFor);
    setDetailProofOpen(proofOpen);
  };

  const quickRun = (t: LteTask, cmd: LteCommand) => {
    command.mutate({ id: t.id, command: cmd, version: t.version });
  };

  const toggleCounterFilter = (
    key: Extract<TerrainFilter, "blocked" | "active" | "planned">,
  ) => {
    setFilter((cur) => (cur === key ? "all" : key));
  };

  return (
    <div className="team-page terrain-screen">
      <ScreenHeader
        title="Exécution terrain"
        sub={
          <>
            Exécution, preuves et clôture des tâches affectées
            <span className="page-note">
              <Icons.cloud size={13} />
              En ligne uniquement : rien n'est mis en attente sur l'appareil
            </span>
          </>
        }
      />
      <div>
        <div className="kpi-row" style={{ padding: 0 }}>
          <button
            className={`kpi-counter blocked${filter === "blocked" ? " active" : ""}`}
            aria-pressed={filter === "blocked"}
            onClick={() => toggleCounterFilter("blocked")}
          >
            <span className="n">{counts.blocked}</span> bloquée
            {counts.blocked > 1 ? "s" : ""}
          </button>
          <button
            className={`kpi-counter running${filter === "active" ? " active" : ""}`}
            aria-pressed={filter === "active"}
            onClick={() => toggleCounterFilter("active")}
          >
            <span className="n">{counts.running}</span> en cours
          </button>
          <button
            className={`kpi-counter planned${filter === "planned" ? " active" : ""}`}
            aria-pressed={filter === "planned"}
            onClick={() => toggleCounterFilter("planned")}
          >
            <span className="n">{counts.planned}</span> planifiée
            {counts.planned > 1 ? "s" : ""}
          </button>
        </div>
      </div>

      <div
        className="member-chips"
        role="group"
        aria-label="Filtrer par statut"
      >
        {FILTER_DEFS.map((f) => (
          <button
            key={f.key}
            className={`member-chip${filter === f.key ? " active" : ""}`}
            aria-pressed={filter === f.key}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isError && (
        <div className="team-error" role="status">
          <Icons.warn size={17} />
          Connexion perdue, nouvelle tentative en cours…
        </div>
      )}

      {isLoading ? (
        <div style={{ marginTop: 16 }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skel-row" style={{ height: 96 }} />
          ))}
        </div>
      ) : (
        <div className={`team-body${isError ? " dimmed" : ""}`}>
          {tasks.length === 0 && !isError ? (
            <div className="empty-state">
              <div className="title">Aucune tâche assignée</div>
              <div className="sub">Tout est en ordre ✓</div>
            </div>
          ) : visible.length === 0 ? (
            <div className="empty-state">
              <div className="title">Aucune tâche pour ce filtre</div>
              <div className="sub">Rien à exécuter ici pour le moment.</div>
            </div>
          ) : (
            <div className="card-list" style={{ padding: "12px 0" }}>
              {visible.map((t) => {
                const closed = LTE_CLOSED_STATUSES.includes(t.status);
                const due = dueText(t);
                return (
                  <div
                    key={t.id}
                    className={`lte-card${closed ? " done" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => openDetail(t.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openDetail(t.id);
                      }
                    }}
                  >
                    <div className="row-line">
                      <Chip meta={chipMeta(LTE_STATUS_META, t.status)} />
                      <Chip meta={chipMeta(LTE_PRIORITY_META, t.priority)} />
                    </div>
                    <div className="ttl">{taskLabel(t)}</div>
                    <div className="lte-meta">
                      {(t.zone_ref || t.site_ref) && (
                        <span>
                          <Icons.pin size={13} />
                          {t.zone_ref || t.site_ref}
                        </span>
                      )}
                      <span>
                        {LTE_TYPE_LABEL[t.execution_type] ?? t.execution_type}
                      </span>
                      {due && <span className="due">{due}</span>}
                    </div>

                    {t.status === "blocked" && (
                      <div className="block-note">
                        Tâche bloquée : ouvrir pour reprendre ou annuler.
                      </div>
                    )}

                    {!closed && (
                      <div
                        className="lte-actions"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        role="presentation"
                      >
                        {t.status === "assigned" && (
                          <>
                            <button
                              className="btn primary"
                              disabled={command.isPending}
                              onClick={() => quickRun(t, "accept")}
                            >
                              Accepter
                            </button>
                            <button
                              className="btn"
                              disabled={command.isPending}
                              onClick={() => openDetail(t.id, "reject")}
                            >
                              Refuser
                            </button>
                          </>
                        )}
                        {t.status === "accepted" && (
                          <button
                            className="btn primary"
                            disabled={command.isPending}
                            onClick={() => quickRun(t, "start")}
                          >
                            Démarrer
                          </button>
                        )}
                        {t.status === "paused" && (
                          <button
                            className="btn primary"
                            disabled={command.isPending}
                            onClick={() => quickRun(t, "resume")}
                          >
                            Reprendre
                          </button>
                        )}
                        {t.status === "started" && (
                          <>
                            <button
                              className="icon-btn"
                              aria-label="Mettre en pause"
                              disabled={command.isPending}
                              onClick={() => quickRun(t, "pause")}
                            >
                              <Icons.pause size={15} />
                            </button>
                            <button
                              className="icon-btn danger"
                              aria-label="Signaler un blocage"
                              onClick={() => openDetail(t.id, "block")}
                            >
                              <Icons.warn size={15} />
                            </button>
                            <button
                              className="btn"
                              onClick={() => openDetail(t.id, null, true)}
                            >
                              Preuve
                            </button>
                            <button
                              className="btn success"
                              disabled={command.isPending}
                              onClick={() => quickRun(t, "complete")}
                            >
                              Terminer
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {selected && (
        <TaskDetailSheet
          key={`${selected.id}-${detailReasonFor ?? ""}-${detailProofOpen}`}
          task={selected}
          initialReasonFor={detailReasonFor}
          initialProofOpen={detailProofOpen}
          onClose={() => {
            setSelectedId(null);
            setDetailReasonFor(null);
            setDetailProofOpen(false);
          }}
        />
      )}
    </div>
  );
}

export default TerrainScreen;
